import { useRef, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Sparkles, Upload, Trash2, ArrowRight, Square, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const MAX_LOGO_BYTES = 1024 * 1024;

export default function Onboarding() {
  const { user } = useAuth();
  const { tenant, saveTenant } = useTenant();
  const navigate = useNavigate();

  const [name, setName] = useState(tenant.workspaceName === "Your Business Portal" ? "" : tenant.workspaceName);
  const [logo, setLogo] = useState(tenant.logo || "");
  const [dragOver, setDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  if (!user) return <Navigate to="/login" replace />;
  if (tenant.onboarded) return <Navigate to="/dashboard" replace />;

  const handleFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file (PNG, JPG, SVG).");
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast.error("Logo too large — max 1 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result;
      if (typeof dataUrl === "string") setLogo(dataUrl);
    };
    reader.onerror = () => toast.error("Failed to read file.");
    reader.readAsDataURL(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const continueOnboarding = () => {
    const finalName = name.trim() || "Your Business Portal";
    setSubmitting(true);
    setTimeout(() => {
      saveTenant({
        workspaceName: finalName,
        logo,
        onboarded: true,
      });
      toast.success(`Welcome to ${finalName.replace(/\s*Portal$/i, "")}!`);
      navigate("/dashboard", { replace: true });
    }, 350);
  };

  const skipForNow = () => {
    saveTenant({ onboarded: true });
    navigate("/dashboard", { replace: true });
  };

  const trimmedName = name.trim() || "Your Business";

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 relative overflow-hidden" data-testid="onboarding-page">
      <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />
      <div className="absolute top-[-15%] right-[-10%] h-[520px] w-[520px] rounded-full bg-primary/15 blur-3xl pointer-events-none" />
      <div className="absolute bottom-[-15%] left-[-10%] h-[520px] w-[520px] rounded-full bg-slate-200/60 dark:bg-slate-800/40 blur-3xl pointer-events-none" />

      <div className="relative w-full max-w-xl px-6">
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 elev-3 p-8 md:p-10 animate-scale-in">
          {/* Header */}
          <div className="flex items-center gap-2 text-[10px] font-semibold text-primary uppercase tracking-[0.2em]">
            <Sparkles className="h-3.5 w-3.5" />
            Set up your portal
          </div>
          <h1 className="mt-3 font-display text-3xl md:text-4xl font-bold text-slate-900 dark:text-slate-100 tracking-tight">
            Let's brand your workspace
          </h1>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
            Add your business name and logo so the portal feels yours from the very first screen. You can change these anytime in Settings.
          </p>

          {/* Live preview */}
          <div className="mt-7 flex items-center gap-3 px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60">
            <div
              className={`h-10 w-10 rounded-lg flex items-center justify-center overflow-hidden shrink-0 ${
                logo ? "bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700" : "bg-primary"
              }`}
              data-testid="onboarding-preview-tile"
            >
              {logo ? (
                <img src={logo} alt="Preview" className="h-full w-full object-contain" />
              ) : (
                <Square className="h-5 w-5 text-white" fill="white" />
              )}
            </div>
            <div className="leading-tight min-w-0">
              <div className="font-display text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                {trimmedName.replace(/\s*Portal$/i, "")}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Portal · live preview
              </div>
            </div>
          </div>

          {/* Business name */}
          <div className="mt-6 space-y-2">
            <Label htmlFor="biz-name" className="text-xs uppercase tracking-wider text-slate-700 dark:text-slate-300 font-medium">
              Business name
            </Label>
            <Input
              id="biz-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Acme Studio"
              data-testid="onboarding-name-input"
              className="h-11 rounded-lg"
              autoFocus
            />
          </div>

          {/* Logo upload */}
          <div className="mt-5 space-y-2">
            <Label className="text-xs uppercase tracking-wider text-slate-700 dark:text-slate-300 font-medium">
              Logo {logo ? "" : "(optional)"}
            </Label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => handleFile(e.target.files?.[0])}
              className="hidden"
              data-testid="onboarding-logo-input"
            />
            <div
              data-testid="onboarding-dropzone"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              className={`flex items-center gap-4 px-4 py-4 rounded-xl border-2 border-dashed cursor-pointer transition-colors ${
                dragOver
                  ? "border-primary bg-primary/5"
                  : "border-slate-200 dark:border-slate-800 hover:border-slate-400 dark:hover:border-slate-600 bg-slate-50/50 dark:bg-slate-900/40"
              }`}
            >
              <div
                className={`h-14 w-14 rounded-lg flex items-center justify-center overflow-hidden shrink-0 ${
                  logo ? "bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700" : "bg-slate-100 dark:bg-slate-800"
                }`}
              >
                {logo ? (
                  <img src={logo} alt="Logo" className="h-full w-full object-contain" />
                ) : (
                  <Upload className="h-5 w-5 text-slate-500 dark:text-slate-400" strokeWidth={1.75} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                  {logo ? "Looking great" : "Drop your logo here"}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  {logo ? "Click to replace · PNG, JPG, SVG up to 1 MB" : "or click to browse · PNG, JPG, SVG up to 1 MB"}
                </div>
              </div>
              {logo && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLogo("");
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                  data-testid="onboarding-remove-logo"
                  className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-lg shrink-0"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                  Remove
                </Button>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="mt-8 flex items-center justify-between gap-3 pt-6 border-t border-slate-100 dark:border-slate-800">
            <button
              type="button"
              onClick={skipForNow}
              data-testid="onboarding-skip"
              className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"
            >
              Skip for now
            </button>
            <Button
              type="button"
              onClick={continueOnboarding}
              disabled={submitting}
              data-testid="onboarding-continue"
              className="h-11 px-6 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground font-medium btn-premium group"
            >
              {submitting ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Setting up...
                </>
              ) : (
                <>
                  Continue
                  <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-0.5" />
                </>
              )}
            </Button>
          </div>
        </div>

        <p className="text-center text-xs text-slate-500 dark:text-slate-400 mt-6">
          You can change branding anytime from Settings → Customization.
        </p>
      </div>
    </div>
  );
}
